// PlanningTab.js (React - part of teacher-frontend using Tailwind CSS)
import React, { useState, useEffect } from 'react';
import axios from 'axios';
import html2pdf from "html2pdf.js";

export default function PlanningTab() {
  const [subject, setSubject] = useState('');
  const [topic, setTopic] = useState('');
  const [yearGroup, setYearGroup] = useState('');
  const [duration, setDuration] = useState('');
  const [lessonType, setLessonType] = useState('Introductory');
  const [generatedPlan, setGeneratedPlan] = useState(null);
  const [savedPlans, setSavedPlans] = useState([]);
  const [subjectFilter, setSubjectFilter] = useState("");
  const [yearFilter, setYearFilter] = useState("");
  const [selectedFiles, setSelectedFiles] = useState({});
  const [planFileLists, setPlanFileLists] = useState({});


  const fetchSavedPlans = async () => {
    const res = await axios.get('/api/lesson-plans');
    setSavedPlans(res.data);
  };

  useEffect(() => {
    fetchSavedPlans();
  }, []);

  const handleGenerate = async () => {
    const res = await axios.post('/api/generate-lesson-plan', {
      subject, topic, year_group: yearGroup, duration, lesson_type: lessonType
    });
    setGeneratedPlan(res.data);
  };

  const handleSave = async () => {
    await axios.post('/api/save-lesson-plan', {
      subject, topic, year_group: yearGroup, content: generatedPlan
    });
    fetchSavedPlans();
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Are you sure you want to delete this plan?")) return;

    try {
      await axios.delete(`/api/lesson-plan/${id}`);
      setSavedPlans(savedPlans.filter(p => p.id !== id));
    } catch (err) {
      console.error("Failed to delete", err);
      alert("Error deleting plan");
    }
  };

  const handleExportToPDF = (plan) => {
    const element = document.createElement("div");
    element.innerHTML = `
      <h2 style="font-size: 18px; font-weight: bold;">${plan.subject} - ${plan.topic}</h2>
      <p><strong>Year Group:</strong> ${plan.year_group}</p>
      <pre style="white-space: pre-wrap;">${plan.content.plan}</pre>
      <p style="font-size: 12px; margin-top: 10px;">Generated on ${new Date(plan.created_at).toLocaleDateString()}</p>
    `;

    html2pdf().set({
      margin: 1,
      filename: `${plan.subject}-${plan.topic}.pdf`,
      html2canvas: { scale: 2 },
      jsPDF: { unit: "in", format: "a4", orientation: "portrait" },
    }).from(element).save();
  };

  const handleFileSelect = (e, planId) => {
    setSelectedFiles({ ...selectedFiles, [planId]: e.target.files[0] });
  };

  const handleFileUpload = async (e, planId) => {
    e.preventDefault();
    const file = selectedFiles[planId];
    if (!file) return alert("Please select a file first.");
    const formData = new FormData();
    formData.append("file", file);
    try {
      await axios.post(`/api/upload-plan-file`, formData, {
        headers: { "Content-Type": "multipart/form-data" }
      });
      alert("File uploaded!");
      fetchPlanFiles(planId); // <-- fetch files after upload
    } catch (err) {
      alert("Upload failed");
    }
  };

  const fetchPlanFiles = async (planId) => {
    try {
      const res = await axios.get(`/api/lesson-plan/${planId}/files`);
      setPlanFileLists(prev => ({ ...prev, [planId]: res.data }));
    } catch {
      setPlanFileLists(prev => ({ ...prev, [planId]: [] }));
    }
  };

  useEffect(() => {
    savedPlans.forEach(plan => fetchPlanFiles(plan.id));
  }, [savedPlans]);

  return (
    <div className="p-6 space-y-6">
      <h2 className="text-3xl font-bold text-gray-800">AI Lesson Planner</h2>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <input
          className="border border-gray-300 p-3 rounded w-full"
          type="text"
          placeholder="Subject"
          value={subject}
          onChange={e => setSubject(e.target.value)}
        />
        <input
          className="border border-gray-300 p-3 rounded w-full"
          type="text"
          placeholder="Topic"
          value={topic}
          onChange={e => setTopic(e.target.value)}
        />
        <input
          className="border border-gray-300 p-3 rounded w-full"
          type="text"
          placeholder="Year Group"
          value={yearGroup}
          onChange={e => setYearGroup(e.target.value)}
        />
        <input
          className="border border-gray-300 p-3 rounded w-full"
          type="text"
          placeholder="Duration (e.g., 45 mins)"
          value={duration}
          onChange={e => setDuration(e.target.value)}
        />
        <select
          className="border border-gray-300 p-3 rounded w-full"
          value={lessonType}
          onChange={e => setLessonType(e.target.value)}
        >
          <option>Introductory</option>
          <option>Practice</option>
          <option>Assessment</option>
          <option>Revision</option>
        </select>
      </div>

      <button
        onClick={handleGenerate}
        className="bg-blue-600 text-white px-6 py-2 rounded hover:bg-blue-700"
      >
        Generate Plan
      </button>

      {generatedPlan && (
        <div className="bg-white border border-gray-200 rounded shadow p-5 mt-6">
          <h3 className="text-xl font-semibold text-gray-700 mb-2">Generated Lesson Plan</h3>
          <pre className="whitespace-pre-wrap text-sm text-gray-800">{generatedPlan.plan}</pre>
          <button
            onClick={handleSave}
            className="mt-4 bg-green-600 text-white px-4 py-2 rounded hover:bg-green-700"
          >
            Save Plan
          </button>
        </div>
      )}

      <div className="mt-10">
        <h2 className="text-2xl font-bold text-gray-800 mb-4">Saved Plans</h2>
        <div className="flex gap-4 mb-4">
          <input
            type="text"
            placeholder="Filter by subject..."
            className="border border-gray-300 p-2 rounded w-1/2"
            value={subjectFilter}
            onChange={e => setSubjectFilter(e.target.value)}
          />
          <input
            type="text"
            placeholder="Filter by year group..."
            className="border border-gray-300 p-2 rounded w-1/2"
            value={yearFilter}
            onChange={e => setYearFilter(e.target.value)}
          />
        </div>
        <div className="space-y-4">
          {savedPlans
            .filter(plan =>
              plan.subject.toLowerCase().includes(subjectFilter.toLowerCase()) &&
              plan.year_group.toLowerCase().includes(yearFilter.toLowerCase())
            )
            .map((plan, i) => (
              <div key={i} className="bg-gray-50 border border-gray-300 rounded p-4 shadow-sm">
                <p className="font-medium text-gray-700">{plan.subject} — {plan.topic} ({plan.year_group})</p>
                <div className="flex justify-between items-start gap-4">
                  <pre className="whitespace-pre-wrap text-sm text-gray-600">{plan.content.plan}</pre>
                  <div>
                    <button
                      onClick={() => handleDelete(plan.id)}
                      className="text-red-500 hover:text-red-700 text-sm font-semibold"
                      title="Delete plan"
                    >
                      🗑️ Delete
                    </button>
                    <button
                      onClick={() => handleExportToPDF(plan)}
                      className="text-blue-600 hover:text-blue-800 text-sm font-semibold ml-2"
                      title="Export as PDF"
                    >
                      📄 Export
                    </button>
                  </div>
                </div>
                <form onSubmit={(e) => handleFileUpload(e, plan.id)} className="mt-2 flex items-center gap-2">
                  <input
                    type="file"
                    onChange={(e) => handleFileSelect(e, plan.id)}
                    className="text-sm"
                  />
                  <button type="submit" className="text-green-600 hover:text-green-800 text-sm">
                    📤 Upload File
                  </button>
                </form>
                {planFileLists[plan.id] && planFileLists[plan.id].length > 0 && (
                  <div className="mt-2 text-sm">
                    <span className="font-medium">Uploaded files:</span>
                    <ul className="list-disc ml-5">
                      {planFileLists[plan.id].map(filename => (
                        <li key={filename}>
                          <a
                            href={`/uploads/plans/${plan.id}/${filename}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-blue-700 underline"
                          >
                            {filename}
                          </a>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            ))}
        </div>
      </div>
    </div>
  );
}

